import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useProfile } from '../hooks/useProfile';
import * as XLSX from 'xlsx';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

interface Row {
  id: string;
  year: string;
  month: string;
  returnPct: string;
  aum: string;
}

interface FundMeta {
  id: string;
  name: string;
  inception_date: string | null;
}

interface ExistingPerf {
  year: number;
  month: number;
  return_pct: number;
  aum: number | null;
}

function newRow(): Row {
  const now = new Date();
  return {
    id: Math.random().toString(36).slice(2),
    year: now.getFullYear().toString(),
    month: (now.getMonth() + 1).toString(),
    returnPct: '',
    aum: '',
  };
}

export default function UpdatePerformance() {
  const navigate = useNavigate();
  const { firm } = useProfile();
  const [funds, setFunds] = useState<FundMeta[]>([]);
  const [selectedFund, setSelectedFund] = useState('');
  const [existingPerf, setExistingPerf] = useState<ExistingPerf[]>([]);
  const [rows, setRows] = useState<Row[]>([newRow()]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [error, setError] = useState('');
  const [fundsLoaded, setFundsLoaded] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Load funds
  useEffect(() => {
    if (firm && !fundsLoaded) {
      supabase
        .from('funds')
        .select('id, name, inception_date')
        .eq('firm_id', firm.id)
        .then(({ data }) => {
          if (data) {
            setFunds(data);
            const params = new URLSearchParams(window.location.search);
            const preselect = params.get('fund');
            if (preselect && data.find((f: FundMeta) => f.id === preselect)) {
                setSelectedFund(preselect);
            } else if (data.length > 0) {
                setSelectedFund(data[0].id);
            }
          }
          setFundsLoaded(true);
        });
    }
  }, [firm, fundsLoaded]);

  // When selected fund changes, fetch existing performance
  useEffect(() => {
    if (!selectedFund) return;
    supabase
      .from('fund_performance')
      .select('year, month, return_pct, aum')
      .eq('fund_id', selectedFund)
      .order('year', { ascending: true })
      .order('month', { ascending: true })
      .then(({ data }) => {
        setExistingPerf(data || []);
      });
  }, [selectedFund]);

  const updateRow = (id: string, field: keyof Row, value: string) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const addRow = () => setRows(prev => [...prev, newRow()]);

  const removeRow = (id: string) => {
    if (rows.length === 1) return;
    setRows(prev => prev.filter(r => r.id !== id));
  };

  // Generate all months from inception to today
  const generateMonthsFromInception = (inceptionDate: string | null): { year: number; month: number }[] => {
    const months: { year: number; month: number }[] = [];
    const start = inceptionDate ? new Date(inceptionDate) : new Date();
    const end = new Date();
    let current = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
    while (current <= endMonth) {
      months.push({ year: current.getFullYear(), month: current.getMonth() + 1 });
      current.setMonth(current.getMonth() + 1);
    }
    return months;
  };

  // Download Excel template pre-filled with inception months + existing data
  const downloadTemplate = () => {
    const fund = funds.find(f => f.id === selectedFund);
    const months = generateMonthsFromInception(fund?.inception_date || null);

    const templateData = months.map(({ year, month }) => {
      const existing = existingPerf.find(p => p.year === year && p.month === month);
      return {
        Year: year,
        Month: month,
        'Return (%)': existing ? existing.return_pct : '',
        'AUM ($mm)': existing?.aum ?? '',
      };
    });

    const ws = XLSX.utils.json_to_sheet(templateData);
    ws['!cols'] = [{ wch: 8 }, { wch: 8 }, { wch: 14 }, { wch: 14 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Performance');

    const instructions = [
      { Instructions: 'Fill in your monthly performance data in the Performance sheet.' },
      { Instructions: 'Year: 4-digit year e.g. 2025' },
      { Instructions: 'Month: Number 1-12 (1=January, 12=December)' },
      { Instructions: 'Return (%): Monthly return as a percentage e.g. 1.45 for +1.45%' },
      { Instructions: 'AUM ($mm): Optional. Assets under management in millions.' },
      { Instructions: 'Existing data is pre-filled. Update any months as needed.' },
    ];
    const wsInstr = XLSX.utils.json_to_sheet(instructions);
    wsInstr['!cols'] = [{ wch: 70 }];
    XLSX.utils.book_append_sheet(wb, wsInstr, 'Instructions');

    const fundName = fund?.name?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'fund';
    XLSX.writeFile(wb, `altveno_${fundName}_performance.xlsx`);
  };

  // Handle Excel/CSV upload
  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows_raw: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (rows_raw.length === 0) {
          setError('No data found in file.');
          return;
        }

        const imported: Row[] = [];

        for (const row of rows_raw) {
          const keys = Object.keys(row);
          const getVal = (patterns: string[]) => {
            const key = keys.find(k =>
              patterns.some(p => k.toLowerCase().includes(p.toLowerCase()))
            );
            return key ? String(row[key]).trim() : '';
          };

          const year = getVal(['year', 'yr']);
          const month = getVal(['month', 'mon']);
          const returnPct = getVal(['return', 'ret', 'pct', '%']);
          const aum = getVal(['aum', 'assets']);

          if (!year || !month || returnPct === '') continue;
          if (!/^\d{4}$/.test(year)) continue;
          if (isNaN(parseInt(month)) || parseInt(month) < 1 || parseInt(month) > 12) continue;

          imported.push({
            id: Math.random().toString(36).slice(2),
            year,
            month: parseInt(month).toString(),
            returnPct,
            aum,
          });
        }

        if (imported.length > 0) {
          setRows(imported);
          setError('');
        } else {
          setError('Could not parse file. Make sure columns are: Year, Month, Return (%), AUM ($mm)');
        }
      } catch (err) {
        setError('Failed to read file. Please use the provided Excel template.');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);

    const invalid = rows.find(r => !r.year || !r.month || r.returnPct === '');
    if (invalid) {
      setError('Please fill in year, month, and return % for all rows.');
      setLoading(false);
      return;
    }

    const records = rows.map(r => ({
      fund_id: selectedFund,
      year: parseInt(r.year),
      month: parseInt(r.month),
      return_pct: parseFloat(r.returnPct),
      aum: r.aum ? parseFloat(r.aum) : null,
    }));

    const { error } = await supabase
      .from('fund_performance')
      .upsert(records, { onConflict: 'fund_id,year,month' });

    if (error) {
      setError(error.message);
    } else {
      setSuccessCount(records.length);
      setSuccess(true);
      setRows([newRow()]);
    }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%', background: '#FFFFFF',
    border: '1px solid #D0D5DD',
    borderRadius: '3px', color: '#0A0A0A',
    fontSize: '13px', padding: '8px 10px', outline: 'none',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  };

  const selectStyle = { ...inputStyle, cursor: 'pointer' };

  const selectedFundMeta = funds.find(f => f.id === selectedFund);

  return (
    <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh' }}>
      <button onClick={() => navigate(-1)} style={{
        background: '#FFFFFF', border: '1px solid #D0D5DD',
        borderRadius: '3px', color: '#3D4552', padding: '7px 14px',
        cursor: 'pointer', fontSize: '13px', marginBottom: '24px',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>← Back</button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '26px', color: '#0A0A0A', marginBottom: '4px' }}>
            Update performance
          </h1>
          <p style={{ fontSize: '13px', color: '#6B7280' }}>
            Download the pre-filled template, update it and re-upload, or enter data manually
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={downloadTemplate}
            disabled={!selectedFund}
            style={{
              background: '#FFFFFF',
              border: '1px solid #C9A84C',
              borderRadius: '3px', color: '#C9A84C',
              fontSize: '13px', padding: '8px 16px',
              cursor: !selectedFund ? 'not-allowed' : 'pointer',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              opacity: !selectedFund ? 0.5 : 1,
            }}
          >
            ↓ Download template
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileImport}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => fileRef.current?.click()}
            style={{
              background: '#FDF6E7',
              border: '1px solid #C9A84C',
              borderRadius: '3px', color: '#C9A84C',
              fontSize: '13px', padding: '8px 16px',
              cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
            }}
          >
            ↑ Upload Excel / CSV
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#C0222A15', border: '1px solid #C0222A',
          borderRadius: '4px', padding: '12px 14px', marginBottom: '16px',
          fontSize: '13px', color: '#C0222A'
        }}>{error}</div>
      )}

      {success && (
        <div style={{
          background: '#0A7C4215', border: '1px solid #0A7C42',
          borderRadius: '4px', padding: '12px 14px', marginBottom: '16px',
          fontSize: '13px', color: '#0A7C42'
        }}>✓ {successCount} month{successCount !== 1 ? 's' : ''} saved successfully</div>
      )}

      <div style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        {/* Fund selector */}
        <div style={{ marginBottom: '20px', maxWidth: '320px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '6px' }}>Fund</label>
          <select style={selectStyle} value={selectedFund} onChange={e => setSelectedFund(e.target.value)}>
            {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          {selectedFundMeta?.inception_date && (
            <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '6px' }}>
              Inception: {new Date(selectedFundMeta.inception_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
              {' · '}{existingPerf.length} months on record
            </div>
          )}
        </div>

        {/* Column headers */}
        <div style={{ display: 'grid', gridTemplateColumns: '110px 160px 140px 160px 36px', gap: '8px', marginBottom: '8px', padding: '0 4px' }}>
          {['Year', 'Month', 'Return (%)', 'AUM ($mm)', ''].map((h, i) => (
            <div key={i} style={{ fontSize: '11px', color: '#6B7280' }}>{h}</div>
          ))}
        </div>

        {/* Rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {rows.map(row => (
            <div key={row.id} style={{ display: 'grid', gridTemplateColumns: '110px 160px 140px 160px 36px', gap: '8px', alignItems: 'center' }}>
              <select style={selectStyle} value={row.year} onChange={e => updateRow(row.id, 'year', e.target.value)}>
                {[2020, 2021, 2022, 2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select style={selectStyle} value={row.month} onChange={e => updateRow(row.id, 'month', e.target.value)}>
                {monthNames.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
              <input
                style={inputStyle}
                type="number"
                step="0.01"
                placeholder="e.g. 1.45"
                value={row.returnPct}
                onChange={e => updateRow(row.id, 'returnPct', e.target.value)}
              />
              <input
                style={inputStyle}
                type="number"
                step="0.1"
                placeholder="optional"
                value={row.aum}
                onChange={e => updateRow(row.id, 'aum', e.target.value)}
              />
              <button
                onClick={() => removeRow(row.id)}
                disabled={rows.length === 1}
                style={{
                  background: 'transparent',
                  border: '1px solid #D0D5DD',
                  borderRadius: '3px', color: '#6B7280',
                  fontSize: '14px', width: '36px', height: '36px',
                  cursor: rows.length === 1 ? 'not-allowed' : 'pointer',
                  opacity: rows.length === 1 ? 0.3 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >×</button>
            </div>
          ))}
        </div>

        {/* Add row */}
        <button
          onClick={addRow}
          style={{
            marginTop: '12px',
            background: 'transparent',
            border: '1px dashed #D0D5DD',
            borderRadius: '3px', color: '#6B7280',
            fontSize: '13px', padding: '8px 16px',
            cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
            width: '100%'
          }}
        >+ Add row</button>

        {/* Save button */}
        <button
          onClick={handleSave}
          disabled={loading || !selectedFund}
          style={{
            width: '100%', marginTop: '20px',
            background: '#0A1628', border: 'none',
            borderRadius: '3px', color: '#FFFFFF',
            fontSize: '14px', fontWeight: 600, padding: '12px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? 'Saving...' : `Save ${rows.length} month${rows.length !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
}